import { InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import {
  EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
  STANDARD_ALLOWED_ENTITY_TYPES,
} from '@unifyapps/carbon/no-code/utils/interfaceQueryFilters';
import type { CustomBlockEntity } from '@unifyapps/defs/blocks/Custom/types';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  getFindEntityByIdQueryKey,
  getSearchInterfacePageEmbeddedEntitiesQueryKey,
  searchInterfacePageEmbeddedEntities,
  useFindEntityById,
  useSearchEntities,
  useSearchInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { EmbeddedDependentsResponse } from '@unifyapps/network/generated/models/embeddedDependentsResponse';
import { UAError } from '@unifyapps/network/utils/consts/error';
import { useMemo } from 'react';
import { debugSSR } from '../../../../../../../consts/debug';
import { useEnvVariables } from '../../../../../../../hooks/useEnvVariables';
import { useFetchInterfacePermissions } from '../../../../../../access-control/hooks/useFetchInterfacePermissions';
import { NO_INTERFACE_RECORD_ID } from '../../../../../../const';
import { NO_INTERFACE_RECORD } from '../../../../../../const/dummy';
import InterfaceHelper from '../../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { normalizeEmbeddedDependents } from '../../../../../../utils/normalizeEmbeddedDependents';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../../types';
import { useEvictQueriesOnMount } from '../../../invalidation/useEvictQueriesOnMount';
import { useFetchInterfaceCommonNamespace } from '../../useFetchInterfaceNamespaces';

const useFetchStandardInterfaceAppResources: UseFetchInterfaceAppResourcesHookType = ({
  interfaceId,
  mode,
  client,
  fetchOnMount,
}) => {
  const isNoInterface = interfaceId === NO_INTERFACE_RECORD_ID;
  const isBuilder = mode === InterfaceModes.BUILDER;

  const globalInterfacePageId = isNoInterface
    ? ''
    : (InterfaceHelper.getGlobalInterfacePageComponent(interfaceId).id ?? '');

  // One embedded-entities search returns the global page entity AND every data source it
  // references — replaces the legacy split (Component search + DataSource search by component id).
  const globalInterfacePageRequest = {
    entityId: globalInterfacePageId,
    // exclude diffs from page component responses in non-builder mode to reduce payload size
    excludeFields: isBuilder ? undefined : EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
    allowedEntityTypes: STANDARD_ALLOWED_ENTITY_TYPES,
  };

  // collaboration freshness: builder routes opt in via fetchOnMount so the interface record
  // and global-page embedded queries are evicted on the first render — the observers below
  // then read an empty cache and refetch, giving every builder mount a fresh snapshot. WS
  // patches never touch this client's React Query cache, so without this a stale
  // pre-navigation snapshot would leak in. See useEvictQueriesOnMount for the loader-gating
  // contract (loader shows on the mount-fetch, not on later external invalidations).
  const shouldEvictOnMount = fetchOnMount === true && !isNoInterface;
  useEvictQueriesOnMount(
    [
      getFindEntityByIdQueryKey(UAEntityTypes.Interface, interfaceId),
      getSearchInterfacePageEmbeddedEntitiesQueryKey(globalInterfacePageRequest),
    ],
    shouldEvictOnMount,
  );

  const {
    data: interfaceRecordFromApi,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
  } = useFindEntityById<InterfaceEntity>(UAEntityTypes.Interface, interfaceId ?? '', {
    query: {
      enabled: Boolean(interfaceId) && !isNoInterface,
      retry: 3,
      staleTime: Infinity,
    },
  });

  const interfaceRecord = isNoInterface ? NO_INTERFACE_RECORD : interfaceRecordFromApi;
  const parentInterfaceId = interfaceRecord?.properties?.parentInterfaceId;

  const {
    data: parentInterfaceRecord,
    isLoading: isLoadingParentInterfaceRecord,
    error: errorParentInterfaceRecord,
  } = useFindEntityById<InterfaceEntity>(UAEntityTypes.Interface, parentInterfaceId ?? '', {
    query: {
      enabled: Boolean(parentInterfaceId),
      retry: 3,
      staleTime: Infinity,
    },
  });

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useFetchInterfacePermissions({
    interfaceId,
    client,
    mode,
  });

  const {
    envVariables,
    isLoading: envVariablesLoading,
    error: envVariablesError,
  } = useEnvVariables();

  const {
    commonNamespace,
    isLoading: isLoadingCommonNamespace,
    error: errorCommonNamespace,
  } = useFetchInterfaceCommonNamespace({
    interfaceId,
    mode,
    interfaceRecord,
  });
  const {
    data: embeddedResp,
    isLoading: isLoadingGlobalInterfacePage,
    error: errorGlobalInterfacePage,
  } = useSearchInterfacePageEmbeddedEntities(globalInterfacePageRequest, {
    query: {
      enabled: Boolean(globalInterfacePageId) && !isNoInterface,
      retry: 3,
      staleTime: Infinity,
      // A missing global page is an expected state, not a failure: older apps never had one, and
      // in builder useCreateGlobalInterfacePageEffect lazily creates it on first mount. The
      // backend returns ASSET_NOT_FOUND (5002) for it, and an errored query is exempt from
      // staleTime — React Query refires it on every remount, so during first-page creation the
      // load/error toggles unmount and remount the app shell in a loop. Resolving 5002 as an
      // empty success (normalizeEmbeddedDependents yields undefined page + empty dataSources)
      // caches it under staleTime: Infinity like the pre-5002 backend contract, so it refetches
      // only on explicit invalidation (post-create invalidation, builder evict-on-mount).
      // Genuine failures still reject and retry.
      queryFn: async ({ signal, meta }) => {
        try {
          return await searchInterfacePageEmbeddedEntities(
            globalInterfacePageRequest,
            { meta },
            signal,
          );
        } catch (error) {
          const errorCode = (error as { payload?: { errorCode?: UAError } } | undefined)?.payload
            ?.errorCode;
          if (errorCode === UAError.ASSET_NOT_FOUND) {
            return {} as EmbeddedDependentsResponse;
          }
          throw error;
        }
      },
    },
  });

  const { interfacePage: globalInterfacePage, dataSources } = useMemo(
    () => normalizeEmbeddedDependents(embeddedResp),
    [embeddedResp],
  );

  // Workspace-wide custom block library (NOT page-scoped) - kept on its own search call.
  // This is intentionally separate from page-level custom blocks: the app shell needs the
  // full library so newly-dropped blocks render without a refetch.
  const { data: customBlocks, isLoading: isCustomBlockLoading } = useSearchEntities(
    UAEntityTypes.CustomBlock,
    { page: { limit: 1000, offset: 0 } },
    {
      query: {
        staleTime: Infinity,
        select: (customBlockHit) => customBlockHit.objects as CustomBlockEntity[] | undefined,
      },
    },
  );

  const _isLoading =
    isLoadingInterfaceRecord ||
    isLoadingParentInterfaceRecord ||
    permissionsLoading ||
    envVariablesLoading ||
    isLoadingCommonNamespace ||
    isLoadingGlobalInterfacePage ||
    isCustomBlockLoading;

  const _error =
    errorInterfaceRecord ||
    errorParentInterfaceRecord ||
    permissionsError ||
    envVariablesError ||
    errorCommonNamespace ||
    errorGlobalInterfacePage;

  debugSSR(
    'useFetchStandardInterfaceAppResources',
    {
      isLoading: _isLoading,
      isLoadingInterfaceRecord,
      isLoadingGlobalInterfacePage,
      isLoadingCommonNamespace,
      isLoadingPermissions: permissionsLoading,
      isLoadingEnvVariables: envVariablesLoading,
      isCustomBlockLoading,
    },
    {
      interfaceId: interfaceRecord?.id,
    },
  );

  return {
    isLoading: _isLoading,
    error: _error,
    interfaceRecord,
    permissions,
    envVariables,
    commonNamespace,
    dataSources,
    globalInterfacePage,
    customBlocks,
    parentInterfaceRecord,
  };
};

export default useFetchStandardInterfaceAppResources;
